import { useEffect, useMemo, useState } from 'react';
import { api, ApiError, type WithdrawHistoryItem, type WithdrawView } from '../api';
import { PinPad } from '../components/PinPad';
import { CopyText, Empty, Field, Header, Notice, Rows, Tabs, WalletId } from '../components/ui';
import { alertUser, haptic } from '../telegram';

const TRC20_RE = /^T[1-9A-HJ-NP-Za-km-z]{25,34}$/;

type Tab = 'usdt' | 'rmb';

function feeFor(view: WithdrawView, amount: string): number {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return view.feeUsdt / 1e6;
  return view.feeUsdt / 1e6 + (value * view.feeBps) / 10_000;
}

export function WithdrawPage({ navigate }: { navigate: (hash: string) => void }) {
  const [tab, setTab] = useState<Tab>('usdt');
  const [view, setView] = useState<WithdrawView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<WithdrawHistoryItem[] | null>(null);

  const load = (): void => {
    api
      .withdrawView()
      .then((data) => {
        setView(data);
        if (data.bindAddressOnly && data.bindAddress) setAddress(data.bindAddress);
      })
      .catch((err: ApiError) => setLoadError(err.message));
  };

  useEffect(load, []);

  useEffect(() => {
    if (showHistory && history === null) {
      api
        .withdrawHistory()
        .then((data) => setHistory(data.items))
        .catch((err: ApiError) => alertUser(err.message));
    }
  }, [showHistory, history]);

  const fee = useMemo(() => (view ? feeFor(view, amount) : 0), [view, amount]);
  const receive = useMemo(() => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) return 0;
    return Math.max(0, value - fee);
  }, [amount, fee]);

  const canSubmit =
    Boolean(view?.enabled) && TRC20_RE.test(address.trim()) && Number(amount) > 0 && !busy;

  const submit = async (pin: string): Promise<void> => {
    if (!view) return;
    setBusy(true);
    // The request is isolated from the post-success UI work: a rendering or
    // Telegram SDK hiccup must never be reported as a failed withdrawal.
    let result: Awaited<ReturnType<typeof api.submitWithdraw>>;
    try {
      result = await api.submitWithdraw({ address: address.trim(), amount, pin });
    } catch (err) {
      const apiError = err as ApiError;
      haptic('error');
      if (
        apiError.code === 'PIN_WRONG' ||
        apiError.code === 'PIN_REQUIRED' ||
        apiError.code === 'PIN_INVALID_FORMAT'
      ) {
        setPinError(apiError.message);
      } else {
        setPinOpen(false);
        setPinError(null);
        setError(apiError.message);
        if (apiError.code === 'PIN_NOT_SET') {
          alertUser('请先设置 6 位安全密码');
          navigate('#/security');
        }
      }
      setBusy(false);
      return;
    }

    setBusy(false);
    setPinOpen(false);
    setPinError(null);
    setError(null);
    setAmount('');
    setHistory(null);
    load();
    haptic('success');
    alertUser(
      `提现申请已提交\n单号：${result.id}\n金额：${result.amountText} USDT\n状态：${result.statusText}`,
    );
  };

  const onSubmitClick = (): void => {
    setError(null);
    if (!view) return;
    if (!TRC20_RE.test(address.trim())) {
      setError('提现地址格式不正确，请检查 USDT-TRC20 地址');
      return;
    }
    if (!view.hasPin) {
      alertUser('请先设置 6 位安全密码');
      navigate('#/security');
      return;
    }
    setPinError(null);
    setPinOpen(true);
  };

  if (loadError) {
    return (
      <div className="app">
        <Header title="提现" />
        <div className="body">
          <div className="error">{loadError}</div>
        </div>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="app">
        <Header title="提现" />
        <div className="loading">加载中…</div>
      </div>
    );
  }

  if (showHistory) {
    return (
      <div className="app">
        <Header title="提现记录" onBack={() => setShowHistory(false)} />
        <div className="body">
          {history === null ? (
            <div className="loading">加载中…</div>
          ) : history.length === 0 ? (
            <Empty text="暂时没有数据哦~" />
          ) : (
            <div className="list">
              {history.map((item) => (
                <div className="item" key={item.id}>
                  <div className="top">
                    <b>{item.amountText} USDT</b>
                    <span className={`badge ${item.status}`}>{item.statusText}</span>
                  </div>
                  <div className="meta mono">{item.address}</div>
                  <div className="meta">
                    到账 {item.receiveText} USDT · 手续费 {item.feeText} ·{' '}
                    {new Date(item.createdAt).toLocaleString('zh-CN')}
                  </div>
                  <div className="meta mono">单号 {item.id}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header
        title="提现"
        action={
          <span className="link" onClick={() => setShowHistory(true)}>
            记录
          </span>
        }
      />
      <WalletId id={view.walletId} pinReady={view.hasPin} />
      <Tabs
        tabs={[
          { key: 'usdt', label: 'USDT提现' },
          { key: 'rmb', label: 'RMB提现' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'usdt' ? (
        <div className="body">
          <Field
            label="提现地址"
            hint={
              <>
                <span>
                  币种：<b>“USDT TRC20”</b>
                </span>
                <span className={view.bindAddressOnly ? '' : 'suffix'}>
                  提现走绑定地址 {view.bindAddressOnly ? '开' : '关'}
                </span>
              </>
            }
          >
            <input
              className="mono"
              placeholder="请输入您的提现地址"
              value={address}
              spellCheck={false}
              autoCapitalize="none"
              disabled={view.bindAddressOnly && Boolean(view.bindAddress)}
              onChange={(event) => setAddress(event.target.value)}
            />
          </Field>

          <Field label="提现金额（USDT）">
            <input
              inputMode="decimal"
              placeholder="请输入提现金额"
              value={amount}
              onChange={(event) => setAmount(event.target.value.replace(/[^\d.]/g, ''))}
            />
            <span className="suffix">USDT</span>
            <span
              className="suffix action"
              onClick={() => setAmount(view.balanceText.replace(/,/g, ''))}
            >
              全部
            </span>
          </Field>

          <Rows
            items={[
              ['可用余额', `${view.balanceText} USDT`],
              ['手续费', `${fee.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')} USDT`],
              ['预计到账', `${receive.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')} USDT`],
            ]}
          />

          <div className="hint">
            单笔最低 {view.minUsdtText} USDT，最高 {view.maxUsdtText} USDT
          </div>

          <Notice>请仔细核对提现地址与网络，避免造成资产损失</Notice>
          {error ? <div className="error">{error}</div> : null}
          {view.bindAddress ? (
            <div className="hint">
              已绑定地址：<CopyText value={view.bindAddress} />
            </div>
          ) : null}

          <button type="button" className="primary" disabled={!canSubmit} onClick={onSubmitClick}>
            立即提现
          </button>
        </div>
      ) : (
        <div className="body">
          {view.fiat.enabled ? (
            <>
              <div className="hint">
                RMB 提现通过换汇通道完成，单笔最低 {view.fiat.minUsdtText} USDT。
              </div>
              {view.fiat.notice ? <Notice>{view.fiat.notice}</Notice> : null}
              <button type="button" className="primary" onClick={() => navigate('#/fx?d=U2CNY')}>
                前往 USDT → 人民币
              </button>
            </>
          ) : (
            <>
              <Empty text="即将开放" />
              <div className="hint" style={{ justifyContent: 'center' }}>
                法币提现通道尚未开通，可先使用换汇将 USDT 兑换为人民币。
              </div>
              <button type="button" className="ghost" onClick={() => navigate('#/fx')}>
                前往换汇
              </button>
            </>
          )}
        </div>
      )}

      {pinOpen ? (
        <PinPad
          title="请输入安全密码"
          subtitle="提现需验证 6 位安全密码"
          error={pinError}
          onSubmit={(pin) => void submit(pin)}
          onCancel={() => {
            setPinOpen(false);
            setPinError(null);
          }}
        />
      ) : null}
    </div>
  );
}
